'use client'

import './globals.css'
import { useState } from 'react'
import { usePathname } from 'next/navigation'
import { Providers } from './providers'
import { Header } from '@/components/layout/Header'
import { Sidebar, SidebarProvider, useSidebar } from '@/components/layout/Sidebar'
import { MobileSidebar } from '@/components/layout/MobileSidebar'
import { DataStateWatcher } from '@/components/layout/DataStateWatcher'
import { DocumentTitle } from '@/components/layout/DocumentTitle'
import { VisualPattern } from '@/components/brand'
import { PointerGlow, POINTER_GLOW_ENABLED } from '@/components/brand/PointerGlow'
import { Funnel_Display, Geologica } from 'next/font/google'
import { cn } from '@/lib/utils'

// Configure Funnel Display (Primary / Headings)
const funnel = Funnel_Display({
  subsets: ['latin'],
  variable: '--font-funnel',
  display: 'swap',
})

// Configure Geologica (Secondary / Body)
const geologica = Geologica({
  subsets: ['latin'],
  variable: '--font-geologica',
  display: 'swap',
})

// Routes that should NOT show the main app shell (sidebar, header)
const AUTH_ROUTES = ['/auth/signin', '/auth/register', '/auth/error', '/customer-portal']

// Inner layout that can access sidebar context and pathname
function LayoutContent({ children }: { children: React.ReactNode }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { isCollapsed } = useSidebar()
  const pathname = usePathname()

  // Check if current route is an auth route (should be full-screen)
  const isAuthRoute = AUTH_ROUTES.some((route) => pathname?.startsWith(route))

  // Auth routes get a clean, full-screen layout (page handles its own layout)
  if (isAuthRoute) {
    return (
      <main className='min-h-screen'>
        <DocumentTitle
          fallback={pathname?.startsWith('/customer-portal') ? 'Customer Portal | AR Command Center' : 'AR Command Center | Supervity'}
        />
        {children}
      </main>
    )
  }

  // Normal app layout with sidebar and header
  return (
    <>
      {/* Browser tab title follows the route and the breadcrumb. */}
      <DocumentTitle />

      {/* Ambient visual pattern */}
      <VisualPattern variant='subtle' />

      {/* Pointer glow for the presenter. Signed-in shell only, never on auth
          routes; z 40, under the header, rail and overlays. */}
      {POINTER_GLOW_ENABLED && <PointerGlow />}

      {/* Desktop Sidebar */}
      <Sidebar />

      {/* Mobile Sidebar */}
      <MobileSidebar
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />

      {/* Main content area */}
      <div
        className={cn(
          'min-h-screen',
          // Sticky page toolbars sit below the floating header (top-4 + h-14) with a 12px gap.
          '[--shell-sticky-top:5.25rem]',
          // Offset for fixed sidebar - responds to collapse state
          isCollapsed ? 'md:pl-16' : 'md:pl-64',
          // Transition for sidebar collapse
          'transition-all duration-300 ease-out'
        )}
      >
        {/* Floating Header */}
        <Header onOpenMobileMenu={() => setMobileMenuOpen(true)} />

        {/* Main content with landmark role */}
        <main
          id='main-content'
          role='main'
          aria-label='Main content'
          className={cn(
            'flex flex-col',
            // Account for floating header (h-14 + top-4 + gap)
            'pt-24',
            // Padding
            'px-4 pb-8 lg:px-8',
            // Min height for full viewport
            'min-h-screen'
          )}
          tabIndex={-1}
        >
          {children}
        </main>
      </div>

      {/* Reload prompt after a baseline reset, maintenance toast during one. */}
      <DataStateWatcher />
    </>
  )
}

export default function RootLayout({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <html lang='en' className='light' style={{ colorScheme: 'light' }} suppressHydrationWarning>
      <head>
        {/* Light mode only on the main surface (decision D5). */}
        <meta name='color-scheme' content='light' />
        <title>AR Command Center | Supervity</title>
        <meta name='description' content='Where Intelligence Meets Human' />
      </head>
      <body
        className={cn(
          'min-h-screen font-sans antialiased',
          'bg-background text-foreground',
          funnel.variable,
          geologica.variable
        )}
      >
        <Providers>
          <SidebarProvider>
            <LayoutContent>{children}</LayoutContent>
          </SidebarProvider>
        </Providers>
      </body>
    </html>
  )
}
